import { useQuery } from '@tanstack/react-query';
import Loader from '__components/Kizen/Loader';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useSelector } from 'react-redux';
import { getChosenBusiness } from 'store/authentication/selectors';
import { createSetupIntentQuery } from 'query/billing';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SetupForm } from './SetupForm';
import { BusinessAddressStep, hasBusinessAddress } from './BusinessAddressStep';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { gray } from '@kizen/kds/Colors';

type OnPaymentMethodAdded = (
  paymentMethodId: string,
  makePrimary: boolean
) => Promise<void>;

const STRIPE_RETRIES = 3;

const loadStripeWithRetry = async (iterations = 0): Promise<Stripe | null> => {
  const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  if (!STRIPE_PUBLISHABLE_KEY) {
    return null;
  }

  try {
    const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);

    return stripe;
  } catch (error) {
    if (iterations < STRIPE_RETRIES) {
      await new Promise((resolve) =>
        setTimeout(resolve, 400 * (iterations + 1))
      );

      return loadStripeWithRetry(iterations + 1);
    } else {
      monitoringExceptionHelper(
        `Failed to load Stripe.js after ${STRIPE_RETRIES} attempts`,
        { originalException: error }
      );

      return null;
    }
  }
};

const STEPS = {
  ADDRESS: 'address',
  PAYMENT: 'payment',
} as const;

type Step = (typeof STEPS)[keyof typeof STEPS];

// Each step registers its submit handler so both can share BasicModal's standard footer
export type StepSubmitRef = MutableRefObject<(() => Promise<void>) | undefined>;

const PaymentMethodStep = (props: {
  submitRef: StepSubmitRef;
  onAddressRequired: () => void;
  existingIds: string[];
  onSuccess: OnPaymentMethodAdded;
  showMakePrimary?: boolean;
}) => {
  const {
    submitRef,
    onAddressRequired,
    existingIds,
    onSuccess,
    showMakePrimary,
  } = props;
  const chosenBusiness = useSelector(getChosenBusiness);
  const [stripePromise] = useState(loadStripeWithRetry);

  const { data, isLoading, error } = useQuery(
    createSetupIntentQuery({
      gcTime: 0,
      throwOnError: false,
      retry: false,
    })
  );

  const isAddressError = (error as AxiosError)?.response?.status === 400;

  useEffect(() => {
    if (isAddressError) {
      onAddressRequired();
    }
  }, [isAddressError, onAddressRequired]);

  const stripeOptions = useMemo(() => {
    if (!data) {
      return;
    }

    return {
      appearance: {
        variables: {
          colorText: gray['gray-800'],
          colorTextSecondary: gray['gray-800'],
          fontSizeBase: '14px',
          fontFamily: '"Proxima Nova", sans-serif',
        },
      },
      clientSecret: data?.client_secret,
    };
  }, [data]);

  if (isLoading || !stripeOptions) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader loading paddingTop="0" />
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={stripeOptions}>
      <SetupForm
        submitRef={submitRef}
        existingIds={existingIds}
        defaultCountry={chosenBusiness?.country}
        defaultPostalCode={chosenBusiness?.postal_code}
        onSuccess={onSuccess}
        showMakePrimary={showMakePrimary}
      />
    </Elements>
  );
};

export const AddCardModal = (props: any) => {
  const { existingIds, onSuccess, showMakePrimary, ...modalProps } = props;
  const { t } = useTranslation();
  const chosenBusiness = useSelector(getChosenBusiness);

  const [step, setStep] = useState<Step>(() =>
    hasBusinessAddress(chosenBusiness) ? STEPS.PAYMENT : STEPS.ADDRESS
  );

  const submitRef: StepSubmitRef = useRef();

  const goToStep = useCallback((nextStep: Step) => {
    submitRef.current = undefined;
    setStep(nextStep);
  }, []);
  const goToAddress = useCallback(() => goToStep(STEPS.ADDRESS), [goToStep]);
  const goToPayment = useCallback(() => goToStep(STEPS.PAYMENT), [goToStep]);

  const handleConfirm = useCallback(async () => {
    await submitRef.current?.();
  }, []);

  return (
    <BasicModal
      {...modalProps}
      heading={t('Add Payment Method')}
      buttonText={step === STEPS.ADDRESS ? t('Save & Continue') : t('Save')}
      onConfirm={handleConfirm}
      showLoadingIndicator
    >
      <div className="min-h-[410px] flex flex-col">
        {step === STEPS.ADDRESS ? (
          <BusinessAddressStep submitRef={submitRef} onSaved={goToPayment} />
        ) : (
          <PaymentMethodStep
            submitRef={submitRef}
            onAddressRequired={goToAddress}
            existingIds={existingIds}
            onSuccess={onSuccess}
            showMakePrimary={showMakePrimary}
          />
        )}
      </div>
    </BasicModal>
  );
};
